import React, { useState, useEffect, useCallback } from 'react';
import settlementService from '../../services/settlementService';

export const RefundWorkflowTab = ({
  user,
  showToast,
  onOpenReceipt
}) => {
  const isAccountsOrAdmin =
    user?.roleName?.toUpperCase() === 'ADMINISTRATOR' ||
    user?.roleId === 1 ||
    user?.roleName?.toLowerCase().includes('account');

  // Active status filter tab
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'Pending Approval', 'Approved', 'Processed', 'Rejected', 'aging'
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [ageFilter, setAgeFilter] = useState('all'); // 'all', '0', '1-2', '3+'

  // Data states
  const [loading, setLoading] = useState(false);
  const [refunds, setRefunds] = useState([]);

  // Modals
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [processModalOpen, setProcessModalOpen] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);

  // Request form state
  const [reqLabNo, setReqLabNo] = useState('');
  const [reqReceiptNo, setReqReceiptNo] = useState('');
  const [reqAmount, setReqAmount] = useState('');
  const [reqReason, setReqReason] = useState('');
  const [reqMode, setReqMode] = useState('Cash');
  const [reqBankName, setReqBankName] = useState('');
  const [reqCardNo, setReqCardNo] = useState('');
  const [reqTxnId, setReqTxnId] = useState('');
  const [submittingReq, setSubmittingReq] = useState(false);

  // Approve / Reject / Process form state
  const [approvalRemarks, setApprovalRemarks] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processMode, setProcessMode] = useState('Cash');
  const [processTxnId, setProcessTxnId] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  // Load Refunds
  const loadRefunds = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: search.trim() || undefined,
        status: activeTab !== 'all' && activeTab !== 'aging' ? activeTab : undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        minAgeDays: ageFilter === '1-2' ? 1 : ageFilter === '3+' ? 3 : undefined
      };

      const res = await settlementService.getRefundRequests(params);
      if (res.success && res.data) {
        let items = res.data;
        if (activeTab === 'aging') {
          // Sort descending by age for aging view
          items = [...items].sort((a, b) => b.ageInDays - a.ageInDays);
        }
        if (ageFilter === '0') {
          items = items.filter((r) => r.ageInDays === 0);
        } else if (ageFilter === '1-2') {
          items = items.filter((r) => r.ageInDays >= 1 && r.ageInDays <= 2);
        } else if (ageFilter === '3+') {
          items = items.filter((r) => r.ageInDays >= 3);
        }
        setRefunds(items);
      } else {
        showToast(res.message || 'Failed to load refund requests', 'error');
      }
    } catch (err) {
      console.error('Error loading refunds:', err);
      showToast('Error connecting to refund service', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, activeTab, fromDate, toDate, ageFilter, showToast]);

  useEffect(() => {
    loadRefunds();
  }, [loadRefunds]);

  // Handle Create Request
  const handleCreateRequest = async (e) => {
    e.preventDefault();
    if (!reqReceiptNo.trim()) {
      showToast('Please enter original receipt number', 'error');
      return;
    }
    const amt = parseFloat(reqAmount);
    if (isNaN(amt) || amt <= 0) {
      showToast('Please enter a valid refund amount greater than 0', 'error');
      return;
    }
    if (!reqReason.trim()) {
      showToast('Please enter a mandatory refund reason', 'error');
      return;
    }

    setSubmittingReq(true);
    try {
      const payload = {
        labNo: reqLabNo.trim(),
        originalReceiptNo: reqReceiptNo.trim(),
        refundAmount: amt,
        refundReason: reqReason.trim(),
        refundPaymentMode: reqMode,
        bankName: reqBankName.trim() || undefined,
        cardNo: reqCardNo.trim() || undefined,
        transactionId: reqTxnId.trim() || undefined
      };

      const res = await settlementService.createRefundRequest(payload);
      if (res.success) {
        showToast(`Refund request #${res.data?.refundRequestNo || ''} created successfully!`, 'success');
        setRequestModalOpen(false);
        // Reset form
        setReqLabNo('');
        setReqReceiptNo('');
        setReqAmount('');
        setReqReason('');
        setReqBankName('');
        setReqCardNo('');
        setReqTxnId('');
        loadRefunds();
      } else {
        showToast(res.message || 'Failed to create refund request', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Error creating refund request', 'error');
    } finally {
      setSubmittingReq(false);
    }
  };

  // Handle Approve
  const handleApprove = async () => {
    if (!selectedRefund) return;
    setProcessingAction(true);
    try {
      const res = await settlementService.approveRefundRequest({
        refundRequestId: selectedRefund.refundRequestId,
        approvalRemarks: approvalRemarks.trim() || undefined
      });
      if (res.success) {
        showToast('Refund request approved successfully', 'success');
        setApproveModalOpen(false);
        setSelectedRefund(null);
        setApprovalRemarks('');
        loadRefunds();
      } else {
        showToast(res.message || 'Failed to approve refund', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Error approving refund', 'error');
    } finally {
      setProcessingAction(false);
    }
  };

  // Handle Reject
  const handleReject = async () => {
    if (!selectedRefund) return;
    if (!rejectionReason.trim()) {
      showToast('Rejection reason is mandatory', 'error');
      return;
    }
    setProcessingAction(true);
    try {
      const res = await settlementService.rejectRefundRequest({
        refundRequestId: selectedRefund.refundRequestId,
        rejectionReason: rejectionReason.trim()
      });
      if (res.success) {
        showToast('Refund request rejected with reason recorded', 'success');
        setRejectModalOpen(false);
        setSelectedRefund(null);
        setRejectionReason('');
        loadRefunds();
      } else {
        showToast(res.message || 'Failed to reject refund', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Error rejecting refund', 'error');
    } finally {
      setProcessingAction(false);
    }
  };

  // Handle Process
  const handleProcess = async () => {
    if (!selectedRefund) return;
    setProcessingAction(true);
    try {
      const res = await settlementService.processApprovedRefund({
        refundRequestId: selectedRefund.refundRequestId,
        refundPaymentMode: processMode,
        transactionId: processTxnId.trim() || undefined
      });
      if (res.success) {
        showToast(
          `Refund processed! Receipt #${res.data?.refundReceiptNo || ''} generated. Historical transaction preserved.`,
          'success'
        );
        setProcessModalOpen(false);
        setSelectedRefund(null);
        setProcessTxnId('');
        loadRefunds();
      } else {
        showToast(res.message || 'Failed to process refund', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Error processing refund', 'error');
    } finally {
      setProcessingAction(false);
    }
  };

  // Compute live breakdown counts
  const counts = React.useMemo(() => {
    let pending = 0;
    let approved = 0;
    let processed = 0;
    let rejected = 0;
    let delayed = 0;
    let totalValue = 0;

    refunds.forEach((r) => {
      if (r.status === 'Pending Approval') pending++;
      else if (r.status === 'Approved') approved++;
      else if (r.status === 'Processed') processed++;
      else if (r.status === 'Rejected') rejected++;
      if (r.ageInDays >= 3) delayed++;
      if (r.status === 'Processed' || r.status === 'Approved') {
        totalValue += Number(r.refundAmount || 0);
      }
    });

    return {
      total: refunds.length,
      pending,
      approved,
      processed,
      rejected,
      delayed,
      totalValue
    };
  }, [refunds]);

  return (
    <div className="fin-refunds-container">
      {/* Top Controls & Status Tabs */}
      <div className="fin-subnav-bar">
        <div className="fin-subnav-pills">
          <button
            type="button"
            className={`fin-subnav-btn ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <span>All Requests</span>
            <span className="fin-subnav-count">{counts.total}</span>
          </button>
          <button
            type="button"
            className={`fin-subnav-btn ${activeTab === 'Pending Approval' ? 'active' : ''}`}
            onClick={() => setActiveTab('Pending Approval')}
          >
            <span>⏳ Pending Approval</span>
            {counts.pending > 0 && <span className="fin-subnav-count warn">{counts.pending}</span>}
          </button>
          <button
            type="button"
            className={`fin-subnav-btn ${activeTab === 'Approved' ? 'active' : ''}`}
            onClick={() => setActiveTab('Approved')}
          >
            <span>✓ Approved</span>
            <span className="fin-subnav-count">{counts.approved}</span>
          </button>
          <button
            type="button"
            className={`fin-subnav-btn ${activeTab === 'Processed' ? 'active' : ''}`}
            onClick={() => setActiveTab('Processed')}
          >
            <span>⚡ Processed</span>
            <span className="fin-subnav-count">{counts.processed}</span>
          </button>
          <button
            type="button"
            className={`fin-subnav-btn ${activeTab === 'Rejected' ? 'active' : ''}`}
            onClick={() => setActiveTab('Rejected')}
          >
            <span>✕ Rejected</span>
            <span className="fin-subnav-count">{counts.rejected}</span>
          </button>
          <button
            type="button"
            className={`fin-subnav-btn ${activeTab === 'aging' ? 'active' : ''}`}
            onClick={() => setActiveTab('aging')}
          >
            <span>⏱️ Aging Audit</span>
            {counts.delayed > 0 && <span className="fin-subnav-count alert">{counts.delayed}</span>}
          </button>
        </div>

        <button
          type="button"
          className="fin-btn-brand"
          onClick={() => setRequestModalOpen(true)}
          style={{ height: '38px', padding: '0 16px' }}
        >
          ➕ Request New Refund
        </button>
      </div>

      {/* Filter Toolbar */}
      <div className="fin-cmd-toolbar" style={{ marginTop: '12px' }}>
        <div className="fin-cmd-row">
          <div className="fin-cmd-unit search-unit">
            <span className="fin-cmd-label">Quick Search</span>
            <div className="fin-cmd-input-box">
              <span className="fin-cmd-icon">🔍</span>
              <input
                type="text"
                placeholder="Search Request #, Original Receipt #, Patient Name, Lab #..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="fin-cmd-input"
              />
              {search && (
                <button
                  type="button"
                  className="fin-cmd-clear"
                  onClick={() => setSearch('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="fin-cmd-unit">
            <span className="fin-cmd-label">Transaction Date Range</span>
            <div className="fin-cmd-range">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="fin-cmd-date"
              />
              <span className="range-sep">→</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="fin-cmd-date"
              />
            </div>
          </div>

          <div className="fin-cmd-unit">
            <span className="fin-cmd-label">Aging SLA Filter</span>
            <select
              value={ageFilter}
              onChange={(e) => setAgeFilter(e.target.value)}
              className="fin-cmd-select"
              style={{ minWidth: '150px' }}
            >
              <option value="all">All Age Brackets</option>
              <option value="0">0 Days (Today)</option>
              <option value="1-2">1 - 2 Days Old</option>
              <option value="3+">3+ Days (Delayed / SLA Breached)</option>
            </select>
          </div>

          <div className="fin-cmd-actions">
            <button
              type="button"
              className="fin-btn-ghost"
              onClick={loadRefunds}
              title="Refresh refund records"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Refunds Table Card Wrapper */}
      <div className="fin-grid-card" style={{ marginTop: '14px' }}>
        {/* Instant Financial Metrics Ribbon */}
        <div className="fin-grid-metric-strip">
          <div className="metric-chip">
            <span className="metric-chip-lbl">Filtered Requests</span>
            <span className="metric-chip-val font-bold">{refunds.length} Records</span>
          </div>
          <div className="metric-chip-sep" />
          <div className="metric-chip">
            <span className="metric-chip-lbl">Pending Approval</span>
            <span className={`metric-chip-val font-bold ${counts.pending > 0 ? 'text-rose' : ''}`}>
              {counts.pending} Cases
            </span>
          </div>
          <div className="metric-chip-sep" />
          <div className="metric-chip">
            <span className="metric-chip-lbl">Total Refund Value</span>
            <span className="metric-chip-val text-rose font-bold">
              ₹{counts.totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="metric-chip-sep" />
          <div className="metric-chip">
            <span className="metric-chip-lbl">SLA Alert (&gt;3 Days)</span>
            <span className={`metric-chip-val font-bold ${counts.delayed > 0 ? 'text-rose' : 'text-emerald'}`}>
              {counts.delayed} Breached
            </span>
          </div>
        </div>

        {/* Card Header Strip */}
        <div className="fin-grid-card-head">
          <div className="head-info">
            <span className="head-title">🛡️ Controlled Refund & Aging Audit Ledger</span>
            <span className="head-meta">
              Showing <strong>{refunds.length}</strong> refund requests
              {search && <span> matching <em>"{search}"</em></span>}
            </span>
          </div>
        </div>

        {/* Scrollable Table Viewport */}
        <div className="fin-table-viewport">
          <table className="fin-data-grid">
            <thead>
              <tr>
                <th style={{ minWidth: '135px' }}>Request # & Date</th>
                <th style={{ minWidth: '185px' }}>Original Receipt #</th>
                <th style={{ minWidth: '175px' }}>Patient & Lab #</th>
                <th style={{ minWidth: '110px' }} className="text-right">Original Amt</th>
                <th style={{ minWidth: '130px' }}>Original Collector</th>
                <th style={{ minWidth: '115px' }} className="text-right">Refund Amt</th>
                <th style={{ minWidth: '110px' }}>Refund Mode</th>
                <th style={{ minWidth: '100px' }}>Age SLA</th>
                <th style={{ minWidth: '125px' }}>Workflow Status</th>
                <th style={{ minWidth: '180px' }}>Reason</th>
                <th style={{ minWidth: '220px' }}>Workflow Audit Trail</th>
                <th style={{ minWidth: '120px' }} className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="fin-table-empty">
                    <div className="fin-loading-spinner" />
                    <div>Loading refund records...</div>
                  </td>
                </tr>
              ) : refunds.length === 0 ? (
                <tr>
                  <td colSpan="12" className="fin-table-empty">
                    No refund requests found matching your filter criteria.
                  </td>
                </tr>
              ) : (
                refunds.map((r) => {
                  let ageBadgeClass = 'today';
                  if (r.ageInDays >= 3) ageBadgeClass = 'delayed';
                  else if (r.ageInDays >= 1) ageBadgeClass = 'warning';

                  return (
                    <tr key={r.refundRequestId}>
                      {/* 1. Request # / Date */}
                      <td>
                        <div className="cell-primary font-bold">
                          <span className="fin-req-mono">{r.refundRequestNo || `REQ-${r.refundRequestId}`}</span>
                        </div>
                        <div className="cell-muted" style={{ marginTop: '2px' }}>
                          {new Date(r.requestedAt).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}{' '}
                          {new Date(r.requestedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>

                      {/* 2. Original Receipt # */}
                      <td>
                        <button
                          type="button"
                          className="fin-receipt-btn"
                          onClick={() => onOpenReceipt && onOpenReceipt(r.originalReceiptNo)}
                          title="Click to view & print original receipt voucher"
                        >
                          <span className="receipt-icon">📄</span>
                          <span className="receipt-no-text">{r.originalReceiptNo}</span>
                        </button>
                        <div className="cell-muted" style={{ marginTop: '3px' }}>
                          📅 {new Date(r.originalTransactionDate).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                      </td>

                      {/* 3. Patient & Lab # */}
                      <td>
                        <div className="cell-primary font-bold">{r.patientName}</div>
                        <div className="cell-mono-uhid">UHID: {r.patientId}</div>
                        <div className="cell-lab-mono" style={{ marginTop: '2px' }}>{r.labNo}</div>
                      </td>

                      {/* 4. Original Amount */}
                      <td className="text-right">
                        <div className="font-bold text-slate">
                          ₹{r.originalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </div>
                        <span className={`fin-mode-tag ${r.originalPaymentMode.toLowerCase().replace(/\s+/g, '')}`} style={{ marginTop: '3px' }}>
                          {r.originalPaymentMode}
                        </span>
                      </td>

                      {/* 5. Original Collector (Preserved) */}
                      <td>
                        <div className="cell-primary font-medium">{r.originalCollectorName || 'Front Office'}</div>
                        <div className="cell-muted">👤 Original Cashier</div>
                      </td>

                      {/* 6. Refund Amount */}
                      <td className="text-right">
                        <span className="fin-paid-badge refund">
                          -₹{r.refundAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      </td>

                      {/* 7. Refund Mode */}
                      <td>
                        <span className={`fin-mode-tag ${r.refundPaymentMode.toLowerCase().replace(/\s+/g, '')}`}>
                          {r.refundPaymentMode === 'Cash' ? '💵' : r.refundPaymentMode === 'UPI' ? '📱' : '💳'} {r.refundPaymentMode}
                        </span>
                      </td>

                      {/* 8. Age SLA */}
                      <td>
                        <span className={`fin-aging-pill ${ageBadgeClass}`}>
                          {r.ageInDays === 0 ? '● Today (0d)' : r.ageInDays <= 2 ? `● ${r.ageInDays}d Old` : `⚠️ ${r.ageInDays}d Delayed`}
                        </span>
                      </td>

                      {/* 9. Workflow Status */}
                      <td>
                        <span className={`fin-status-pill ${r.status.toLowerCase().replace(/\s+/g, '')}`}>
                          {r.status === 'Pending Approval' ? '⏳ Pending' : r.status === 'Approved' ? '✓ Approved' : r.status === 'Processed' ? '⚡ Processed' : '✕ Rejected'}
                        </span>
                        {r.rejectionReason && (
                          <div className="fin-micro-reason" title={r.rejectionReason}>
                            Rej: {r.rejectionReason}
                          </div>
                        )}
                      </td>

                      {/* 10. Reason */}
                      <td className="cell-investigation" title={r.refundReason}>
                        <span className="fin-inv-pill">
                          📑 {r.refundReason}
                        </span>
                      </td>

                      {/* 11. Workflow Audit Details */}
                      <td>
                        <div className="fin-workflow-timeline">
                          <div className="fin-timeline-step">
                            <span className="step-bullet">1</span>
                            <span className="step-text"><strong>Req:</strong> {r.requestedByName}</span>
                          </div>
                          {r.approvedByName && (
                            <div className="fin-timeline-step approved">
                              <span className="step-bullet">2</span>
                              <span className="step-text"><strong>Appr:</strong> {r.approvedByName} ({new Date(r.approvedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})</span>
                            </div>
                          )}
                          {r.rejectedByName && (
                            <div className="fin-timeline-step rejected">
                              <span className="step-bullet">✕</span>
                              <span className="step-text"><strong>Rej:</strong> {r.rejectedByName}</span>
                            </div>
                          )}
                          {r.processedByName && (
                            <div className="fin-timeline-step processed">
                              <span className="step-bullet">3</span>
                              <span className="step-text"><strong>Proc:</strong> {r.processedByName}</span>
                            </div>
                          )}
                          {r.refundReceiptNo && (
                            <div style={{ marginTop: '4px' }}>
                              <button
                                type="button"
                                className="fin-receipt-btn"
                                onClick={() => onOpenReceipt && onOpenReceipt(r.refundReceiptNo)}
                                title="Print refund voucher"
                              >
                                <span className="receipt-icon">📄</span>
                                <span className="receipt-no-text">{r.refundReceiptNo}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* 12. Action Buttons */}
                      <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                        {r.status === 'Pending Approval' && isAccountsOrAdmin && (
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button
                              type="button"
                              className="fin-btn-brand-sm"
                              onClick={() => {
                                setSelectedRefund(r);
                                setApproveModalOpen(true);
                              }}
                              title="Approve refund request"
                            >
                              ✓ Approve
                            </button>
                            <button
                              type="button"
                              className="fin-btn-danger-sm"
                              onClick={() => {
                                setSelectedRefund(r);
                                setRejectModalOpen(true);
                              }}
                              title="Reject refund request"
                            >
                              ✕ Reject
                            </button>
                          </div>
                        )}

                        {r.status === 'Approved' && (
                          <button
                            type="button"
                            className="fin-btn-blue-sm"
                            onClick={() => {
                              setSelectedRefund(r);
                              setProcessMode(r.refundPaymentMode || 'Cash');
                              setProcessModalOpen(true);
                            }}
                            title="Disburse cash/funds and record payout"
                          >
                            ⚡ Process Payout
                          </button>
                        )}

                        {r.status === 'Processed' && r.refundReceiptNo && (
                          <button
                            type="button"
                            className="fin-btn-row-receipt"
                            onClick={() => onOpenReceipt && onOpenReceipt(r.refundReceiptNo)}
                            title="Print official refund voucher"
                          >
                            📄 Voucher
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL 1: Request New Refund */}
      {requestModalOpen && (
        <div className="fin-modal-overlay">
          <div className="fin-modal-content" style={{ maxWidth: '520px' }}>
            <div className="fin-modal-header">
              <h3>Request Patient Refund</h3>
              <button
                type="button"
                className="fin-close-btn"
                onClick={() => setRequestModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateRequest}>
              <div className="fin-modal-body">
                <div className="fin-alert-banner info" style={{ marginBottom: '16px' }}>
                  ℹ️ Cross-day refund rule: The original transaction remains preserved in history on its original date. The refund will be logged under its own timestamp once approved and processed.
                </div>

                <div className="fin-form-group">
                  <label>Original Receipt # *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. RCP-2026-0001"
                    value={reqReceiptNo}
                    onChange={(e) => setReqReceiptNo(e.target.value)}
                    className="fin-input"
                  />
                </div>

                <div className="fin-form-group">
                  <label>Lab / Registration # (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. LAB-202609-001"
                    value={reqLabNo}
                    onChange={(e) => setReqLabNo(e.target.value)}
                    className="fin-input"
                  />
                </div>

                <div className="fin-form-group">
                  <label>Refund Amount (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    placeholder="Enter partial or full refund amount"
                    value={reqAmount}
                    onChange={(e) => setReqAmount(e.target.value)}
                    className="fin-input"
                  />
                </div>

                <div className="fin-form-group">
                  <label>Refund Reason *</label>
                  <textarea
                    required
                    rows="2"
                    placeholder="Provide detailed clinical or administrative reason for refund..."
                    value={reqReason}
                    onChange={(e) => setReqReason(e.target.value)}
                    className="fin-textarea"
                  />
                </div>

                <div className="fin-form-group">
                  <label>Refund Payment Mode</label>
                  <select
                    value={reqMode}
                    onChange={(e) => setReqMode(e.target.value)}
                    className="fin-select"
                  >
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Debit Card">Debit Card</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                  </select>
                </div>

                {reqMode !== 'Cash' && (
                  <div className="fin-form-group">
                    <label>Transaction / Reference / Card No</label>
                    <input
                      type="text"
                      placeholder="Reference ID or last 4 digits"
                      value={reqTxnId}
                      onChange={(e) => setReqTxnId(e.target.value)}
                      className="fin-input"
                    />
                  </div>
                )}
              </div>

              <div className="fin-modal-footer">
                <button
                  type="button"
                  className="fin-btn-outline"
                  onClick={() => setRequestModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="fin-btn-primary"
                  disabled={submittingReq}
                >
                  {submittingReq ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: Approve Refund */}
      {approveModalOpen && selectedRefund && (
        <div className="fin-modal-overlay">
          <div className="fin-modal-content" style={{ maxWidth: '480px' }}>
            <div className="fin-modal-header">
              <h3>Approve Refund Request</h3>
              <button
                type="button"
                className="fin-close-btn"
                onClick={() => setApproveModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="fin-modal-body">
              <p>Are you sure you want to approve this refund request?</p>
              <div className="fin-detail-box" style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', fontSize: '13px' }}>
                <div><strong>Request:</strong> {selectedRefund.refundRequestNo}</div>
                <div><strong>Receipt:</strong> {selectedRefund.originalReceiptNo}</div>
                <div><strong>Patient:</strong> {selectedRefund.patientName}</div>
                <div><strong>Amount:</strong> ₹{selectedRefund.refundAmount.toFixed(2)}</div>
                <div><strong>Reason:</strong> {selectedRefund.refundReason}</div>
              </div>
              <div className="fin-form-group" style={{ marginTop: '12px' }}>
                <label>Approval Remarks (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Verified test cancellation / approved by Accounts"
                  value={approvalRemarks}
                  onChange={(e) => setApprovalRemarks(e.target.value)}
                  className="fin-input"
                />
              </div>
            </div>
            <div className="fin-modal-footer">
              <button
                type="button"
                className="fin-btn-outline"
                onClick={() => setApproveModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="fin-btn-success"
                disabled={processingAction}
                onClick={handleApprove}
              >
                {processingAction ? 'Approving...' : 'Confirm Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: Reject Refund (Mandatory Reason) */}
      {rejectModalOpen && selectedRefund && (
        <div className="fin-modal-overlay">
          <div className="fin-modal-content" style={{ maxWidth: '480px' }}>
            <div className="fin-modal-header">
              <h3>Reject Refund Request</h3>
              <button
                type="button"
                className="fin-close-btn"
                onClick={() => setRejectModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="fin-modal-body">
              <div className="fin-alert-banner alert" style={{ marginBottom: '12px' }}>
                ⚠️ A mandatory rejection reason must be recorded for audit compliance.
              </div>
              <div className="fin-form-group">
                <label>Mandatory Rejection Reason *</label>
                <textarea
                  required
                  rows="3"
                  placeholder="Explain why this refund request is rejected..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="fin-textarea"
                />
              </div>
            </div>
            <div className="fin-modal-footer">
              <button
                type="button"
                className="fin-btn-outline"
                onClick={() => setRejectModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="fin-btn-danger"
                disabled={processingAction || !rejectionReason.trim()}
                onClick={handleReject}
              >
                {processingAction ? 'Rejecting...' : 'Reject Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 4: Process Approved Refund */}
      {processModalOpen && selectedRefund && (
        <div className="fin-modal-overlay">
          <div className="fin-modal-content" style={{ maxWidth: '500px' }}>
            <div className="fin-modal-header">
              <h3>Process Approved Refund</h3>
              <button
                type="button"
                className="fin-close-btn"
                onClick={() => setProcessModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="fin-modal-body">
              <div className="fin-alert-banner success" style={{ marginBottom: '12px' }}>
                ✓ This refund is approved for <strong>₹{selectedRefund.refundAmount.toFixed(2)}</strong>. Processing will generate a negative receipt transaction without overwriting the historical receipt.
              </div>

              <div className="fin-form-group">
                <label>Payout Payment Mode</label>
                <select
                  value={processMode}
                  onChange={(e) => setProcessMode(e.target.value)}
                  className="fin-select"
                >
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Debit Card">Debit Card</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </select>
              </div>

              {processMode !== 'Cash' && (
                <div className="fin-form-group">
                  <label>Transaction / Ref ID</label>
                  <input
                    type="text"
                    placeholder="Enter reference or transaction number"
                    value={processTxnId}
                    onChange={(e) => setProcessTxnId(e.target.value)}
                    className="fin-input"
                  />
                </div>
              )}
            </div>
            <div className="fin-modal-footer">
              <button
                type="button"
                className="fin-btn-outline"
                onClick={() => setProcessModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="fin-btn-primary"
                disabled={processingAction}
                onClick={handleProcess}
              >
                {processingAction ? 'Processing...' : 'Disburse & Record Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundWorkflowTab;
